import { motion } from 'framer-motion';
import { useLanguage } from '@/lib/index';
import { services } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';
import { getServiceData } from '@/lib/serviceData';
import { Globe, Target, Network, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  Globe,
  Target,
  Network,
};

export default function Services() {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen">
      <motion.section
        className="relative bg-charcoal text-charcoal-foreground py-24 md:py-32 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={springPresets.gentle}
      >
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.ARABIC_PATTERN_1}
            alt=""
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/90 via-charcoal/80 to-charcoal" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={staggerItem} className="inline-block mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-semibold text-primary">{t.servicesPage.badge}</span>
              </div>
            </motion.div>

            <motion.h1
              variants={staggerItem}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              {t.servicesPage.hero.title}
              <br />
              <span className="bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
                {t.servicesPage.hero.titleLine2}
              </span>
            </motion.h1>

            <motion.p
              variants={staggerItem}
              className="text-lg md:text-xl text-charcoal-foreground/80 leading-relaxed max-w-3xl mx-auto"
            >
              {t.servicesPage.hero.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        className="bg-cream py-24"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={springPresets.gentle}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto space-y-16">
            {services.map((service, index) => {
              const serviceKey = service.id === 'digital-twin' ? 'digitalTwin' : service.id === 'vertical-strategy' ? 'verticalStrategy' : 'crossDomain';
              const serviceData = getServiceData(language, serviceKey);
              const IconComponent = iconMap[service.icon as keyof typeof iconMap];

              return (
                <motion.div
                  key={service.id}
                  className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <div className="lg:col-span-3">
                    <div className="bg-card rounded-2xl p-8 md:p-10 shadow-sm border border-border/40">
                      <div className="flex items-start gap-4 mb-6">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {IconComponent && <IconComponent className="w-7 h-7 text-primary" />}
                        </div>
                        <div>
                          <h3 className="text-2xl md:text-3xl font-bold mb-2">
                            {t.services[serviceKey].title}
                          </h3>
                          <p className="text-muted-foreground">
                            {t.services[serviceKey].description}
                          </p>
                        </div>
                      </div>

                      <div className="mt-8">
                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                          {t.services[serviceKey].features}
                        </h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {serviceData.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <div className="bg-card rounded-2xl p-8 shadow-sm border border-border/40 h-full">
                      <h4 className="text-lg font-semibold mb-6 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                        </div>
                        <span>{t.servicesPage.benefitsTitle}</span>
                      </h4>
                      <ul className="space-y-4">
                        {serviceData.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                            <span className="text-sm text-muted-foreground">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      <motion.section
        className="bg-background py-24"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={springPresets.gentle}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t.servicesPage.whyChoose.title}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { num: '1', ...t.servicesPage.whyChoose.reason1 },
                { num: '2', ...t.servicesPage.whyChoose.reason2 },
                { num: '3', ...t.servicesPage.whyChoose.reason3 },
                { num: '4', ...t.servicesPage.whyChoose.reason4 },
              ].map((reason, index) => (
                <motion.div
                  key={reason.num}
                  className="bg-card rounded-2xl p-8 shadow-sm border border-border/40"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <span className="text-2xl font-bold text-primary">{reason.num}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{reason.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{reason.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="bg-charcoal text-charcoal-foreground py-24"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={springPresets.gentle}
      >
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t.servicesPage.cta.title}
            </h2>
            <p className="text-lg text-charcoal-foreground/80 mb-8 leading-relaxed">
              {t.servicesPage.cta.description}
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                <span>{t.servicesPage.cta.button}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}